(() => {
  if (customElements.get('erya-category-shelves')) return;
  class EryaCategoryShelves extends HTMLElement {
    connectedCallback() {
      if (this.dataset.navigation !== 'tabs') return;
      this.tabs = Array.from(this.querySelectorAll('[data-commerce-tab]'));
      this.tablist = this.querySelector('[data-commerce-tabs]');
      if (!this.tablist || !this.tabs.length) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tablist.hidden = false;
      this.classList.add('erya-commerce--tabs');
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab), { signal });
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        if (panel) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
        }
      });
      this.tablist.addEventListener('keydown', (event) => {
        const current = this.tabs.indexOf(event.target);
        if (current < 0) return;
        const rtl = getComputedStyle(this.tablist).direction === 'rtl';
        let next = current;
        if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
        else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else return;
        event.preventDefault();
        const tab = this.tabs[(next + this.tabs.length) % this.tabs.length];
        this.activate(tab);
        tab.focus({ preventScroll: true });
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('erya-category-carousel');
        const tab = this.tabs.find((item) => item.getAttribute('aria-controls') === panel?.id);
        if (tab) this.activate(tab);
      }, { signal });
      this.activate(this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0]);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    activate(selected) {
      let activePanel;
      this.tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        if (panel) {
          panel.hidden = !active;
          if (active) activePanel = panel;
        }
      });
      if (activePanel) {
        const viewAll = this.querySelector('[data-commerce-view-all][data-follow-category=true]');
        if (viewAll && selected.dataset.collectionUrl) {
          viewAll.href = selected.dataset.collectionUrl;
          viewAll.setAttribute('aria-label', `${viewAll.dataset.viewAllLabel}: ${selected.textContent.trim()}`);
        }
        activePanel.schedule?.();
        this.dispatchEvent(new CustomEvent('erya:category:change', { bubbles: true, detail: { panel: activePanel, tab: selected } }));
      }
    }
  }
  class EryaCategoryCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-commerce-track]');
      this.items = Array.from(this.querySelectorAll('[data-commerce-item]'));
      this.dataset.count = String(this.items.length);
      this.classList.toggle('erya-commerce-row--sparse', this.items.length < 3);
      if (!this.track || !this.items.length) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.previous = this.querySelector('[data-commerce-prev]');
      this.next = this.querySelector('[data-commerce-next]');
      this.controls = this.querySelector('[data-commerce-controls]');
      this.progress = this.querySelector('[data-commerce-progress]');
      this.counter = this.querySelector('[data-commerce-counter]');
      this.status = this.querySelector('[data-commerce-status]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const direction = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
        if (direction) {
          event.preventDefault();
          this.move(direction);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.goTo(event.key === 'Home' ? 0 : this.items.length - 1);
        }
      }, { signal });
      if ('ResizeObserver' in window) {
        this.observer?.disconnect();
        this.observer = new ResizeObserver(() => this.schedule());
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.schedule(), { passive: true, signal });
      }
      this.schedule();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      clearTimeout(this.announcement);
    }

    schedule() {
      if (!this.track || !this.items?.length || !this.isConnected) return;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    geometry() {
      const viewport = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const cards = this.items.map((item) => item.getBoundingClientRect());
      const distances = cards.map((card) => rtl ? viewport.right - card.right : card.left - viewport.left);
      return { viewport, cards, distances, rtl };
    }

    update() {
      if (!this.isConnected || !this.track || !this.items?.length) return;
      const { viewport, cards, distances, rtl } = this.geometry();
      const shelf = this.track.parentElement;
      const media = this.track.querySelector('.erya-commerce-card__media');
      if (media) {
        const frame = media.getBoundingClientRect();
        shelf.style.setProperty('--commerce-arrow-y', `${Math.round(frame.top - shelf.getBoundingClientRect().top + frame.height / 2)}px`);
      }
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
      this.controls.hidden = !scrollable;
      this.track.tabIndex = scrollable ? 0 : -1;
      const first = cards.findIndex((card) => card.right > viewport.left + 4 && card.left < viewport.right - 4);
      const visible = cards.map((card, index) => ({ card, index })).filter(({ card }) => card.left >= viewport.left - 2 && card.right <= viewport.right + 2);
      const last = visible.length ? visible[visible.length - 1].index : Math.max(first, 0);
      const atStart = distances[0] >= -2;
      const finalCard = cards[cards.length - 1];
      const atEnd = rtl ? finalCard.left >= viewport.left - 2 : finalCard.right <= viewport.right + 2;
      this.previous.disabled = atStart || !scrollable;
      this.next.disabled = atEnd || !scrollable;
      const current = (visible.length ? visible[0].index : Math.max(0, first)) + 1;
      const end = Math.max(current, last + 1);
      this.counter.textContent = `${String(current).padStart(2, '0')} / ${String(this.items.length).padStart(2, '0')}`;
      this.progress.style.transform = `scaleX(${atEnd ? 1 : end / this.items.length})`;
      const range = current === end ? `${current}` : `${current}–${end}`;
      const message = `${this.status.dataset.commerceTitle}: ${range} / ${this.items.length}`;
      clearTimeout(this.announcement);
      this.announcement = setTimeout(() => {
        if (this.status.textContent !== message) this.status.textContent = message;
      }, 180);
    }

    move(direction) {
      const { distances } = this.geometry();
      let target;
      if (direction > 0) {
        target = distances.findIndex((distance) => distance > 4);
        if (target < 0) target = this.items.length - 1;
      } else {
        target = 0;
        distances.forEach((distance, index) => { if (distance < -4) target = index; });
      }
      this.goTo(target);
    }

    goTo(index) {
      const { distances, rtl } = this.geometry();
      const paused = document.body.dataset.pulseMotionState === 'paused' || document.body.dataset.directionMotionState === 'paused' || Boolean(document.querySelector('[data-atelier-motion-toggle][aria-pressed="true"]'));
      this.track.scrollBy({ left: distances[index] * (rtl ? -1 : 1), behavior: this.reducedMotion.matches || paused ? 'instant' : 'smooth' });
    }
  }
  customElements.define('erya-category-carousel', EryaCategoryCarousel);
  customElements.define('erya-category-shelves', EryaCategoryShelves);
  const nativeObservers = new Map();
  const attachNativeArrows = root => {
    root.querySelectorAll('.collection slider-component').forEach(slider => {
      if (nativeObservers.has(slider)) return;
      const measure = () => {
        const media = slider.querySelector('.card__inner');
        if (!media) return;
        const frame = media.getBoundingClientRect();
        slider.style.setProperty('--commerce-arrow-y', `${Math.round(frame.top - slider.getBoundingClientRect().top + frame.height / 2)}px`);
      };
      const observer = new ResizeObserver(measure);
      observer.observe(slider);
      nativeObservers.set(slider, observer);
      measure();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => attachNativeArrows(document), {once:true});
  else attachNativeArrows(document);
  document.addEventListener('shopify:section:load', event => attachNativeArrows(event.target));
  document.addEventListener('shopify:section:unload', event => {
    nativeObservers.forEach((observer, slider) => {
      if (event.target.contains(slider)) {
        observer.disconnect();
        nativeObservers.delete(slider);
      }
    });
  });
})();
